/* =========================================================================
   IBC 2.0 — MENU VIEWER
   The menus are one continuous, horizontally scrollable sequence. The pill
   tab bar is a READ-OUT of that sequence, not a switch: an
   IntersectionObserver watches the panels inside the track and moves
   `aria-selected` / `tabindex` to whichever menu is in view. Nothing here
   ever scrolls the track on its own, so it can never fight the finger.

   Clicking or arrowing to a tab still scrolls the track to that menu.

   Enlarging is not this file's job — the sheet is a real <button> carrying
   `data-dialog-open`, and assets/ibc-dialog.js opens it (focus trap,
   Escape, backdrop click). With JavaScript off the sheets simply stack.

   NO DISCLOSURE
   -------------
   The viewer is open from first paint. The section emits the tab bar, the
   track and the size dock directly, with nothing `hidden` and no button to
   press, so there is no open/closed state for this file to hold, restore or
   animate — and therefore nothing to re-sync once a hidden box acquires
   layout, and no scroll position to hold on the way closed.

   DEEP LINKS
   ----------
   /pages/menu#wine-list still lands on that menu, on the first load and on
   every later hashchange alike. Because the viewer is already open, that is
   simply "select this tab and scroll the track to it" — no navigation-type
   sniffing, no deferred target, no special case for a refresh or a
   back-button restore. `writeHash()` uses replaceState, which does not fire
   `hashchange`, so a tab press cannot loop back through here.
   ========================================================================= */

(function () {
  'use strict';

  /* The section tag is emitted once per section instance, and a duplicated
     <script src> executes again. Bind the document-level listeners once. */
  if (window.ibcMenuViewerBound) return;
  window.ibcMenuViewerBound = true;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

  function behaviour() {
    return reduced.matches ? 'auto' : 'smooth';
  }

  function scrollBox(box, left, instant) {
    var opts = { left: Math.max(0, Math.round(left)), behavior: instant ? 'auto' : behaviour() };
    if (box.scrollTo) box.scrollTo(opts);
    else box.scrollLeft = opts.left;
  }

  function Viewer(root) {
    this.root = root;

    this.track = root.querySelector('[data-ibc-menu-track]');
    this.strip = root.querySelector('[data-ibc-menu-strip]');
    this.tabs = Array.prototype.slice.call(root.querySelectorAll('[data-ibc-menu-tab]'));
    this.panels = Array.prototype.slice.call(root.querySelectorAll('[data-ibc-menu-panel]'));
    if (!this.track || !this.tabs.length || this.tabs.length !== this.panels.length) return;

    this.active = -1;
    this.ratios = this.panels.map(function () {
      return 0;
    });

    this.bindTabs();
    this.bindHash();
    this.watch();

    /* The track is laid out from the start now, so the landing menu is
       chosen and reached here — before the browser has painted, because the
       section loads this file with `defer` and the scroll happens in the
       first animation frame. Nothing arrives late and nothing has to be
       corrected afterwards, so the pill bar is never briefly wrong. */
    var start = this.indexFromHash();
    this.setActive(start, false);
    if (start > 0) {
      var self = this;
      this.scrollToIndex(start, true);
      /* Widths can still be settling on the very first frame. Repeating the
         jump once is idempotent — it cannot land anywhere else — and it
         still runs before the first paint. */
      window.requestAnimationFrame(function () {
        self.scrollToIndex(start, true);
      });
    }
  }

  /* -1 when there is no hash, or the hash names nothing here. */
  Viewer.prototype.hashIndex = function () {
    var hash = (window.location.hash || '').replace(/^#/, '');
    if (!hash) return -1;
    for (var i = 0; i < this.tabs.length; i++) {
      if (this.tabs[i].getAttribute('data-ibc-menu-tab') === hash) return i;
    }
    return -1;
  };

  Viewer.prototype.indexFromHash = function () {
    var i = this.hashIndex();
    return i < 0 ? 0 : i;
  };

  Viewer.prototype.writeHash = function (index) {
    var id = this.tabs[index].getAttribute('data-ibc-menu-tab');
    if (!id || !window.history || !window.history.replaceState) return;
    window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + id);
  };

  /* ---------------------------- deep links ------------------------------ */

  /* An in-page link to a named menu — #wine-list — is somebody asking for
     that menu now. The viewer is already open, so there is nothing to do but
     select the tab and take the track to it. `writeHash()` uses
     replaceState, which does not fire this, so a tab press cannot loop. */
  Viewer.prototype.bindHash = function () {
    var self = this;

    this.onHashChange = function () {
      var i = self.hashIndex();
      if (i < 0) return;
      self.setActive(i, false);
      self.scrollToIndex(i, false);
    };
    window.addEventListener('hashchange', this.onHashChange);
  };

  /* ------------------------------- tabs --------------------------------- */

  Viewer.prototype.bindTabs = function () {
    var self = this;

    this.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        self.setActive(i, true);
        self.scrollToIndex(i, false);
      });

      tab.addEventListener('keydown', function (e) {
        var next = null;
        if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (i + 1) % self.tabs.length;
        if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') next = (i - 1 + self.tabs.length) % self.tabs.length;
        if (e.key === 'Home') next = 0;
        if (e.key === 'End') next = self.tabs.length - 1;
        if (next === null) return;
        e.preventDefault();
        self.setActive(next, true);
        self.scrollToIndex(next, false);
        try {
          self.tabs[next].focus({ preventScroll: true });
        } catch (err) {
          self.tabs[next].focus();
        }
      });
    });
  };

  /* Only the ARIA state changes here — never the track's scroll position. */
  Viewer.prototype.setActive = function (index, updateHash) {
    if (index < 0 || index >= this.tabs.length) return;

    if (index !== this.active) {
      this.active = index;
      this.tabs.forEach(function (tab, i) {
        var on = i === index;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.setAttribute('tabindex', on ? '0' : '-1');
      });
      this.revealTab(index);
    }

    if (updateHash) this.writeHash(index);
  };

  /* Keep the active pill inside the (desktop) scrolling pill strip. This
     moves the strip only — never the page. */
  Viewer.prototype.revealTab = function (index) {
    var strip = this.strip;
    if (!strip || strip.scrollWidth <= strip.clientWidth + 1) return;
    var tab = this.tabs[index];
    var tr = tab.getBoundingClientRect();
    var sr = strip.getBoundingClientRect();
    scrollBox(strip, strip.scrollLeft + (tr.left - sr.left) - (sr.width - tr.width) / 2, false);
  };

  /* ------------------------------ the track ----------------------------- */

  Viewer.prototype.scrollToIndex = function (index, instant) {
    var panel = this.panels[index];
    if (!panel) return;
    var pr = panel.getBoundingClientRect();
    var tr = this.track.getBoundingClientRect();
    scrollBox(this.track, this.track.scrollLeft + (pr.left - tr.left), instant);
  };

  /* Whichever panel occupies most of the track is the active one. */
  Viewer.prototype.watch = function () {
    var self = this;

    if ('IntersectionObserver' in window) {
      this.io = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            var i = self.panels.indexOf(entry.target);
            if (i > -1) self.ratios[i] = entry.intersectionRatio;
          });
          var best = -1;
          var bestRatio = 0;
          for (var i = 0; i < self.ratios.length; i++) {
            if (self.ratios[i] > bestRatio) {
              bestRatio = self.ratios[i];
              best = i;
            }
          }
          if (best > -1 && bestRatio >= 0.4) self.setActive(best, false);
        },
        { root: this.track, threshold: [0, 0.2, 0.4, 0.6, 0.8, 1] }
      );
      this.panels.forEach(function (panel) {
        self.io.observe(panel);
      });
      return;
    }

    /* Fallback: nearest panel centre, measured on a rested scroll. */
    var frame = null;
    this.track.addEventListener(
      'scroll',
      function () {
        if (frame) return;
        frame = window.requestAnimationFrame(function () {
          frame = null;
          self.nearest();
        });
      },
      { passive: true }
    );
    this.nearest();
  };

  Viewer.prototype.nearest = function () {
    var tr = this.track.getBoundingClientRect();
    var centre = tr.left + tr.width / 2;
    var best = -1;
    var bestGap = Infinity;
    this.panels.forEach(function (panel, i) {
      var pr = panel.getBoundingClientRect();
      var gap = Math.abs(pr.left + pr.width / 2 - centre);
      if (gap < bestGap) {
        bestGap = gap;
        best = i;
      }
    });
    if (best > -1) this.setActive(best, false);
  };

  Viewer.prototype.destroy = function () {
    if (this.io) this.io.disconnect();
    /* This one is on `window`, so it would outlive the section otherwise. */
    if (this.onHashChange) window.removeEventListener('hashchange', this.onHashChange);
    this.onHashChange = null;
  };

  /* ------------------------------ wiring -------------------------------- */

  var live = [];

  function init(scope) {
    var roots = (scope || document).querySelectorAll('[data-ibc-menu-viewer]');
    Array.prototype.forEach.call(roots, function (root) {
      if (root.dataset.ibcMenuReady === 'true') return;
      root.dataset.ibcMenuReady = 'true';
      live.push(new Viewer(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  document.addEventListener('shopify:section:load', function (e) {
    live = live.filter(function (viewer) {
      if (viewer.root && !document.contains(viewer.root)) {
        viewer.destroy();
        return false;
      }
      return true;
    });
    init(e.target);
  });

  /* The editor removes a section without ever firing `load` again, and this
     file now holds a `window` listener per viewer. Drop them on the way out
     so nothing binds twice or lingers. */
  document.addEventListener('shopify:section:unload', function (e) {
    live = live.filter(function (viewer) {
      var gone = !viewer.root || !document.contains(viewer.root);
      if (!gone && e.target && e.target.contains) gone = e.target.contains(viewer.root);
      if (!gone) return true;
      viewer.destroy();
      return false;
    });
  });
})();
